import { useMemo } from 'react';
import { useAllSessions } from '../../../stores/prioritization/sessionStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { classifySession, type DashboardFilter } from '../utils/sessionHelpers';

export interface ParticipantSessionGroups {
  needsInput: PrioritizationSession[];
  live: PrioritizationSession[];
  upcoming: PrioritizationSession[];
  completed: PrioritizationSession[];
}

function sessionIncludesUser(s: PrioritizationSession, userId: string): boolean {
  if (!userId) return false;
  if (s.facilitatorId === userId) return true;
  if (s.participantIds?.includes(userId)) return true;
  return false;
}

export function useParticipantSessions() {
  const allSessions = useAllSessions();
  const userId = useUserStore(s => s.userId);
  const userName = useUserStore(s => s.userName);

  const mySessions = useMemo(
    () => allSessions.filter(s => sessionIncludesUser(s, userId)),
    [allSessions, userId],
  );

  const groups = useMemo((): ParticipantSessionGroups => {
    const needsInput: PrioritizationSession[] = [];
    const live: PrioritizationSession[] = [];
    const upcoming: PrioritizationSession[] = [];
    const completed: PrioritizationSession[] = [];

    for (const s of mySessions) {
      const g = classifySession(s);
      if (g === 'completed') completed.push(s);
      else if (g === 'upcoming') upcoming.push(s);
      else if (g === 'live') live.push(s);
      else needsInput.push(s);
    }

    return { needsInput, live, upcoming, completed };
  }, [mySessions]);

  const needsInputCount = groups.needsInput.length + groups.live.length;

  function filterGroups(filter: DashboardFilter): ParticipantSessionGroups {
    if (filter === 'all') return groups;
    return {
      needsInput: filter === 'needs_input' ? groups.needsInput : [],
      live: filter === 'live' ? groups.live : [],
      upcoming: filter === 'upcoming' ? groups.upcoming : [],
      completed: filter === 'completed' ? groups.completed : [],
    };
  }

  return {
    userId,
    userName,
    mySessions,
    groups,
    needsInputCount,
    filterGroups,
  };
}
